/**
 * ModuERP v2.0 — AI chat widget (REST `/api/ai/chat`).
 * Softlavka replies are one-shot; staging WS `/api/ai/chat/ws` fell through to SPA 404.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Box,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import { ApiService } from '@/utils/api';
import { AgentTaskDesk } from '../AgentTasks/AgentTaskDesk';

const AI_CHAT_TIMEOUT_MS = 180_000;

/** Shell chrome (top bar) opens the drawer without a floating FAB. */
export const OPEN_AI_CHAT_EVENT = 'softlavka:open-ai';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  providerId?: string;
}

type ChatApiResponse = {
  content: string;
  provider_id?: string;
  model?: string;
};

type AiWindowMode = 'ask' | 'tasks';

export const AIChatWidget: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<AiWindowMode>('ask');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listEndRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<Message[]>([]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    listEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendText = useCallback(
    (text: string) => {
      if (!text || loading) return;
      setInput('');
      setError(null);
      setLoading(true);

      const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', content: text };
      const nextMessages = [...messagesRef.current, userMsg];
      setMessages(nextMessages);

      const payload = nextMessages.map((m) => ({ role: m.role, content: m.content }));

      void (async () => {
        try {
          const res = await ApiService.post<ChatApiResponse>(
            '/ai/chat',
            { messages: payload, task: 'chat' },
            { timeout: AI_CHAT_TIMEOUT_MS },
          );
          setMessages((prev) => [
            ...prev,
            {
              id: `a-${Date.now()}`,
              role: 'assistant',
              content: res.content || '',
              providerId: res.provider_id,
            },
          ]);
        } catch (e: unknown) {
          const detail =
            typeof e === 'object' && e !== null && 'detail' in e && typeof (e as { detail: unknown }).detail === 'string'
              ? (e as { detail: string }).detail
              : e instanceof Error
                ? e.message
                : t('aiChat.connectionError');
          setError(detail);
        } finally {
          setLoading(false);
        }
      })();
    },
    [loading, t],
  );

  const send = useCallback(() => {
    sendText(input.trim());
  }, [input, sendText]);

  // Bridge from the ⌘K palette's ASK AI row.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail !== 'string') return;
      const text = detail.trim();
      if (!text) return;
      setOpen(true);
      setMode('ask');
      sendText(text);
    };
    window.addEventListener('softlavka:ask-ai', handler);
    return () => window.removeEventListener('softlavka:ask-ai', handler);
  }, [sendText]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(OPEN_AI_CHAT_EVENT, handler);
    return () => window.removeEventListener(OPEN_AI_CHAT_EVENT, handler);
  }, []);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 380 }, maxWidth: '100%' },
        }}
      >
        <Box display="flex" flexDirection="column" height="100%">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={1}
            borderBottom={1}
            borderColor="divider"
          >
            <Typography variant="h6">{t('aiChat.title')}</Typography>
            <IconButton onClick={() => setOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box px={2} py={1} borderBottom={1} borderColor="divider">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              aria-label="Mode"
              onChange={(_event, next: AiWindowMode | null) => {
                if (next) setMode(next);
              }}
            >
              <ToggleButton value="ask">Ask</ToggleButton>
              <ToggleButton value="tasks">Tasks</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {mode === 'ask' ? (
            <>
              <Box flex={1} overflow="auto" px={1} py={2}>
                {messages.length === 0 && (
                  <Typography color="text.secondary" variant="body2" sx={{ px: 1 }}>
                    {t('aiChat.intro')}
                  </Typography>
                )}
                <List dense>
                  {messages.map((m) => (
                    <ListItem
                      key={m.id}
                      alignItems="flex-start"
                      sx={{ flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          maxWidth: '85%',
                          bgcolor: m.role === 'user' ? 'primary.main' : 'action.hover',
                          color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                          borderRadius: 2,
                        }}
                      >
                        <ListItemText
                          primary={m.content}
                          secondary={m.providerId ? `(${m.providerId})` : undefined}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </Paper>
                    </ListItem>
                  ))}
                </List>
                {loading && (
                  <Box display="flex" alignItems="center" gap={1} px={2} py={1}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">{t('aiChat.responding')}</Typography>
                  </Box>
                )}
                {error && (
                  <Typography color="error" variant="caption" sx={{ px: 2 }}>
                    {error}
                  </Typography>
                )}
                <div ref={listEndRef} />
              </Box>
              <Box p={1} borderTop={1} borderColor="divider">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Message..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <IconButton color="primary" onClick={send} disabled={!input.trim() || loading} size="small">
                        <SendIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Box>
            </>
          ) : (
            <Box flex={1} overflow="auto">
              <AgentTaskDesk />
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default AIChatWidget;
